import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const useList = (url) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(url).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [url]);
  return [items, setItems];
};

const Empty = ({ text, testId }) => <p className="card-soft border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid={testId}>{text}</p>;

const Meta = ({ k, v }) => (v ? <p><span className="text-slate-500">{k}: </span><span className="text-ink">{v}</span></p> : null);

export const InquiriesTab = () => {
  const [items, setItems] = useList("/admin/inquiries");
  const update = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Inquiry updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  if (!items.length) return <Empty text="No tour inquiries yet." testId="admin-inquiries-empty" />;
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="admin-inquiries-tab">
      {items.map((i) => (
        <article key={i.id} className="card-soft border border-slate-200 bg-white p-6" data-testid={`admin-inquiry-${i.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-serif text-2xl text-ink">{i.name}</h3>
              <p className="text-xs text-slate-500">{new Date(i.created_at).toLocaleString()} · {i.tour_type.replace("_", " ")} tour</p>
            </div>
            <select value={i.status} onChange={(e) => update(i.id, e.target.value)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs capitalize" data-testid="admin-inquiry-status-select">
              {["new", "contacted", "closed"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="mt-4 space-y-1 text-sm">
            <Meta k="Email" v={<a href={`mailto:${i.email}`} className="underline decoration-gold underline-offset-4">{i.email}</a>} />
            <Meta k="Phone" v={`${i.phone_code || ""} ${i.phone}`.trim()} />
            <Meta k="Date" v={i.date} />
            <Meta k="Pickup location" v={i.pickup_location} />
            <Meta k="Pickup time" v={i.pickup_time} />
            <Meta k="Drop-off time" v={i.dropoff_time} />
            <Meta k="Guests" v={i.guests} />
            <Meta k="Child seats" v={i.child_seats ? i.child_seat_count : null} />
            <Meta k="Hours" v={i.hours} />
            <Meta k="Interests" v={(i.interests || []).join(", ")} />
          </div>
          {i.message && <p className="mt-4 rounded-xl bg-silver p-4 text-sm text-slate-700">{i.message}</p>}
        </article>
      ))}
    </div>
  );
};

export const MessagesTab = () => {
  const [items] = useList("/admin/messages");
  if (!items.length) return <Empty text="No messages yet." testId="admin-messages-empty" />;
  return (
    <div className="grid gap-4" data-testid="admin-messages-tab">
      {items.map((m) => (
        <article key={m.id} className="card-soft border border-slate-200 bg-white p-6" data-testid={`admin-message-${m.id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-serif text-2xl text-ink">{m.name}</h3>
            <span className="text-xs text-slate-500">{new Date(m.created_at).toLocaleString()}</span>
          </div>
          <p className="mt-1 text-sm"><a href={`mailto:${m.email}`} className="underline decoration-gold underline-offset-4">{m.email}</a>{m.phone && ` · ${m.phone}`}</p>
          <p className="mt-4 whitespace-pre-line text-sm text-slate-700">{m.message}</p>
        </article>
      ))}
    </div>
  );
};
